import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  BarChart3,
  TrendingUp,
  Users,
  Clock,
  Download,
  Calendar,
} from 'lucide-react'
import { dashboardApi, api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { PageLayout } from '@/components/layout/PageLayout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  StatCard,
  PipelineFunnelChart,
  SourceEffectivenessChart,
  TimeToHireChart,
  ApplicationsOverTimeChart,
} from '@/components/features/analytics/AnalyticsCharts'

type DateRange = '7d' | '30d' | '90d' | '1y' | 'all'

function getDateRangeParams(range: DateRange): string {
  if (range === 'all') return ''
  const now = Math.floor(Date.now() / 1000)
  const days = range === '7d' ? 7 : range === '30d' ? 30 : range === '90d' ? 90 : 365
  const from = now - days * 86400
  return `?from=${from}&to=${now}`
}

export function ReportsPage() {
  const [dateRange, setDateRange] = useState<DateRange>('30d')

  const rangeParams = getDateRangeParams(dateRange)

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats', dateRange],
    queryFn: dashboardApi.getStats,
  })

  const { data: pipelineApiData } = useQuery({
    queryKey: ['reports-pipeline', dateRange],
    queryFn: () => api.get<any>(`/reports/pipeline${rangeParams}`),
  })

  const { data: sourceApiData } = useQuery({
    queryKey: ['reports-sources', dateRange],
    queryFn: () => api.get<any>(`/reports/sources${rangeParams}`),
  })

  const { data: timeToHireApiData } = useQuery({
    queryKey: ['reports-time-to-hire', dateRange],
    queryFn: () => api.get<any>(`/reports/time-to-hire${rangeParams}`),
  })

  const { data: applicationsOverTimeApiData } = useQuery({
    queryKey: ['reports-applications-over-time', dateRange],
    queryFn: () => api.get<any[]>(`/reports/applications-over-time${rangeParams}`),
  })

  const statsData = stats as any || {}

  const stageColors: Record<string, string> = {
    applied: '#6b7280',
    screening: '#3b82f6',
    interview: '#8b5cf6',
    offer: '#f59e0b',
    hired: '#10b981',
  }

  const pipelineData = (pipelineApiData?.stages || []).map((s: any) => ({
    stage: s.name,
    count: s.count,
    color: stageColors[s.type] || '#6b7280',
  }))

  const sourceData = (sourceApiData?.sources || []).map((s: any) => ({
    source: s.source,
    applications: s.applications,
    hires: s.hired || 0,
    conversionRate: s.conversionRate || 0,
  }))

  const timeToHireData = (timeToHireApiData?.monthly || []).map((m: any) => ({
    month: m.month,
    days: m.days,
  }))

  const applicationsOverTime = (Array.isArray(applicationsOverTimeApiData) ? applicationsOverTimeApiData : []).map((m: any) => ({
    date: m.month || m.date,
    count: m.count,
  }))

  const handleExport = async () => {
    const token = useAuthStore.getState().accessToken
    const API_BASE = import.meta.env.VITE_API_URL ? `${import.meta.env.VITE_API_URL}/api/v1` : '/api/v1'
    const response = await fetch(`${API_BASE}/reports/export?type=applications`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const blob = await response.blob()
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'applications-report.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  const toolbar = (
    <>
      <div className="flex items-center justify-end gap-2">
        <Select value={dateRange} onValueChange={(v: DateRange) => setDateRange(v)}>
          <SelectTrigger className="w-[130px] h-8 text-xs">
            <Calendar className="h-3 w-3 mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
            <SelectItem value="90d">Last 90 days</SelectItem>
            <SelectItem value="1y">Last year</SelectItem>
            <SelectItem value="all">All time</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={handleExport}>
          <Download className="h-3 w-3 mr-1" />
          Export
        </Button>
      </div>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15 }}
        >
          <StatCard
            title="Total Applications"
            value={isLoading ? '...' : statsData.totalApplications || 0}
            icon={Users}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.03 }}
        >
          <StatCard
            title="Interviews"
            value={isLoading ? '...' : statsData.scheduledInterviews || 0}
            icon={Calendar}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.06 }}
        >
          <StatCard
            title="Offers Extended"
            value={isLoading ? '...' : pipelineApiData?.stages?.find((s: any) => s.type === 'offer')?.count || 0}
            icon={TrendingUp}
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.09 }}
        >
          <StatCard
            title="Avg Time to Hire"
            value={isLoading ? '...' : statsData.avgTimeToHire ? `${statsData.avgTimeToHire}d` : 'N/A'}
            icon={Clock}
          />
        </motion.div>
      </div>
    </>
  )

  return (
    <PageLayout fullHeight toolbar={toolbar}>
      {/* Bento Grid */}
      <div className="grid gap-3 grid-cols-1 md:grid-cols-2 lg:grid-cols-6 lg:grid-rows-2 h-full">
        {/* Applications Over Time - top left */}
        <motion.div
          className="lg:col-span-3 min-h-[200px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.1 }}
        >
          <ApplicationsOverTimeChart data={applicationsOverTime} />
        </motion.div>

        {/* Pipeline Funnel - top right */}
        <motion.div
          className="lg:col-span-3 min-h-[200px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.12 }}
        >
          <PipelineFunnelChart data={pipelineData} />
        </motion.div>

        {/* Source Effectiveness - bottom left */}
        <motion.div
          className="lg:col-span-2 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.14 }}
        >
          <SourceEffectivenessChart data={sourceData} />
        </motion.div>

        {/* Time to Hire - bottom center */}
        <motion.div
          className="lg:col-span-1 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.15 }}
        >
          <TimeToHireChart data={timeToHireData} />
        </motion.div>

        {/* Funnel Metrics - bottom right */}
        <motion.div
          className="lg:col-span-3 min-h-[180px] lg:min-h-0"
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.15, delay: 0.15 }}
        >
          <Card className="h-full flex flex-col">
            <CardHeader className="flex-shrink-0 py-3 px-4">
              <CardTitle className="flex items-center gap-2 text-sm">
                <BarChart3 className="h-4 w-4" />
                Hiring Funnel
              </CardTitle>
              <CardDescription className="text-xs">
                Conversion rates at each stage
              </CardDescription>
            </CardHeader>
            <CardContent className="flex-1 min-h-0 overflow-y-auto px-4 pb-3 pt-0">
              {pipelineData.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
                  <BarChart3 className="h-8 w-8 mb-2 opacity-50" />
                  <p className="text-sm">No pipeline data yet</p>
                </div>
              ) : (
              <div className="grid gap-2 grid-cols-2">
                {(() => {
                  const stages = ['applied', 'screening', 'interview', 'offer', 'hired']
                  const stageLabels = ['Application', 'Screening', 'Interview', 'Offer', 'Hired']
                  const rates: { label: string; rate: string }[] = []
                  for (let i = 0; i < stages.length - 1; i++) {
                    const current = pipelineData.find((s: any) => s.stage?.toLowerCase() === stages[i] || s.stage?.toLowerCase().includes(stages[i]))
                    const next = pipelineData.find((s: any) => s.stage?.toLowerCase() === stages[i + 1] || s.stage?.toLowerCase().includes(stages[i + 1]))
                    const rate = current?.count > 0 && next ? Math.round((next.count / current.count) * 100) : 0
                    rates.push({ label: `${stageLabels[i]} → ${stageLabels[i + 1]}`, rate: current?.count > 0 ? `${rate}%` : '—' })
                  }
                  return rates.map((r) => (
                    <div key={r.label} className="text-center p-2 bg-muted/50 rounded-lg">
                      <p className="text-xl font-bold">{r.rate}</p>
                      <p className="text-[10px] text-muted-foreground leading-tight">{r.label}</p>
                    </div>
                  ))
                })()}
              </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </PageLayout>
  )
}
